import React, { Component } from 'react';
import { compose } from 'recompose';
import { MutationFunction } from 'react-apollo';
import { Button } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';

import { Comment, DeleteCommentComponent } from 'api';
import { deleteCommentResponse } from 'apollo/responses';
import { Alert, Error } from 'components';
import {
  withUser,
  withErrorHandler,
  withQueryParams,
  IWithUser,
  IWithErrorHandler,
  IWithQueryParams,
} from 'providers';
import { contentModel } from 'apollo/reducers';

interface IProps {
  comment: Comment;
  isEditing: boolean;
  isReplying: boolean;
  onEdit: () => void;
  onReply: () => void;
}

interface IPropsEnhanced
  extends IProps,
    WithTranslation,
    IWithUser,
    IWithErrorHandler,
    IWithQueryParams,
    WithStyles<typeof styles> {}

interface IState {
  open: boolean;
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      height: 20,
      justifySelf: 'flex-end',
    },
    button: {
      padding: '0 5px',
      fontSize: theme.typography.caption.fontSize,
      minWidth: 'auto',
      minHeight: 'auto',
    },
    bullet: {
      color: theme.custom.palette.grey1,
      fontSize: theme.typography.caption.fontSize,
    },
  });

const enhancers = compose<IPropsEnhanced, IProps>(
  withTranslation(),
  withUser,
  withErrorHandler,
  withQueryParams,
  withStyles(styles),
);

class Actions extends Component<IPropsEnhanced, IState> {
  state: IState = {
    open: false,
  };

  toggleConfirm = () => {
    const { open } = this.state;
    this.setState({ open: !open });
  };

  onDelete = (deleteComment: MutationFunction) => () => {
    const { comment, query, variables } = this.props;

    const commentId = comment.id;
    const contentId = comment.object_id;

    return deleteComment({
      variables: { id: commentId },
      optimisticResponse: deleteCommentResponse(commentId),
      update: (cache, result) => {
        contentModel({ contentId, cache, result, query, variables });
      },
    });
  };

  renderAlert(deleteComment: MutationFunction) {
    const { t } = this.props;
    const { open } = this.state;

    return (
      <Alert
        open={open}
        onConfirm={this.onDelete(deleteComment)}
        onCancel={this.toggleConfirm}
        message={t('content:deleteComment')}
        confirm={t('common:delete')}
      />
    );
  }

  renderDelete() {
    const { classes, t, error, onError, onErrorClose } = this.props;

    return (
      <DeleteCommentComponent onError={onError}>
        {deleteComment => (
          <span>
            {this.renderAlert(deleteComment)}
            <Button onClick={this.toggleConfirm} className={classes.button} size="small">
              {t('common:delete')}
            </Button>
            <Error key={error} message={error} onClose={onErrorClose} />
          </span>
        )}
      </DeleteCommentComponent>
    );
  }

  renderReply() {
    const { classes, t, isReplying, onReply } = this.props;

    return (
      <Button onClick={onReply} className={classes.button} size="small">
        {isReplying && t('content:cancelReply')}
      </Button>
    );
  }

  renderStaticMenu() {
    const { user, classes, comment, t, onEdit } = this.props;
    const canWrite = comment.user && comment.user.id === user.id;

    return (
      <div className={classes.container}>
        {canWrite && (
          <span>
            <Button onClick={onEdit} className={classes.button} size="small">
              {t('common:edit')}
            </Button>
            <span className={classes.bullet}>&bull;</span>
            {this.renderDelete()}
            <span className={classes.bullet}>&bull;</span>
          </span>
        )}
        {this.renderReply()}
      </div>
    );
  }

  renderEditMenu() {
    const { classes, t, onEdit } = this.props;

    return (
      <div className={classes.container}>
        <Button onClick={onEdit} className={classes.button} size="small">
          {t('common:cancel')}
        </Button>
      </div>
    );
  }

  render() {
    const { isEditing } = this.props;

    return isEditing ? this.renderEditMenu() : this.renderStaticMenu();
  }
}

export default enhancers(Actions);
